import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, Save, Delete } from '@mui/icons-material';
import axios from '../../config/axios';
import ReservableFormFields from '../../components/reservables/ReservableFormFields';
import ReservablePreviewCard from '../../components/reservables/ReservablePreviewCard';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import {
  reservableToForm,
  formToPayload,
  formToPreviewReservable,
  validateReservableForm,
} from '../../utils/reservableHelpers';

const ReservableDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [reservableRes, usersRes] = await Promise.all([
          axios.get(`/api/reservables/${id}`),
          axios.get('/api/user'),
        ]);
        setForm(reservableToForm(reservableRes.data));
        setTeachers(usersRes.data.filter((u) => u.roles?.includes('teacher')));
      } catch (err) {
        setError('Risorsa non trovata o errore di caricamento');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const preview = useMemo(
    () => (form ? formToPreviewReservable(form, teachers) : null),
    [form, teachers]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    const validationErrors = validateReservableForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setSaving(true);
      await axios.put(`/api/reservables/${id}`, formToPayload(form));
      setResultModal({
        open: true,
        message: 'Risorsa aggiornata con successo',
        severity: 'success',
      });
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Errore durante il salvataggio';
      setResultModal({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/reservables/${id}`);
      navigate('/manage-reservables');
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Impossibile eliminare la risorsa';
      setResultModal({ open: true, message, severity: 'error' });
    } finally {
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !form) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/manage-reservables')}>
          Torna all&apos;elenco
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/manage-reservables')}
        sx={{ mb: 2 }}
      >
        Torna all&apos;elenco
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Modifica risorsa
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {form.name}
          </Typography>
        </Box>
        <Button
          color="error"
          variant="outlined"
          startIcon={<Delete />}
          onClick={() => setConfirmDelete(true)}
        >
          Elimina
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSave}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper sx={{ p: 3 }}>
              <ReservableFormFields
                form={form}
                errors={errors}
                teachers={teachers}
                onChange={setForm}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button onClick={() => navigate('/manage-reservables')}>
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
                  disabled={saving}
                >
                  Salva modifiche
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper sx={{ p: 2, position: { lg: 'sticky' }, top: 88 }}>
              {preview && <ReservablePreviewCard reservable={preview} />}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <ModalConfirm
        open={confirmDelete}
        title="Elimina risorsa"
        message={`Eliminare definitivamente "${form.name}"? Consentito solo senza prenotazioni attive.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
      <ModalResult
        open={resultModal.open}
        onClose={() => setResultModal({ open: false, message: '', severity: 'info' })}
        message={resultModal.message}
        severity={resultModal.severity}
      />
    </Container>
  );
};

export default ReservableDetail;
